import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import CardWithShadow from '@components/CardWithShadow/CardWithShadow';
import Loadable from '@components/Loadable/Loadable';
import { TSupplementResponseStatus } from '@interfaces/supplements';

import SupplementNeedsInfo from './SupplementNeedsInfo/SupplementNeedsInfo';
import SupplementResponseDetailsBody from './SupplementResponseDetailsBody/SupplementResponseDetailsBody';
import { ISupplementResponseDetailsProps } from './SupplementResponseDetails.interface';

const SupplementResponseDetails: FC<ISupplementResponseDetailsProps> = ({
  className,
  data,
  isLoading,
  error,
  hasUnseenResponses,
  isLastOneThatNeedsInfo,
  onShowRiskLevelModal,
  onSubmitNeedsInfoForm,
  onClose,
  onRetry,
}) => {
  const { t } = useTranslation();

  const supplementNeedsInfo =
    data?.supplementInquiryStatusId === TSupplementResponseStatus.NeedsInfo;

  const handleShowRiskLevelModal = useCallback(() => {
    data?.riskLevelId && onShowRiskLevelModal(data.riskLevelId);
  }, [data, onShowRiskLevelModal]);

  return (
    <CardWithShadow className={cn('supplement-response-details', className)}>
      <Loadable
        isLoading={isLoading}
        error={error && t('errors.supplementResponseDetailsError')}
        onRetry={onRetry}
      >
        {supplementNeedsInfo && data?.userEnteredSupplementId ? (
          <SupplementNeedsInfo
            userEnteredSupplementId={data.userEnteredSupplementId}
            hasUnseenResponses={hasUnseenResponses}
            isDeactivated={!data.isActive}
            isLastOneThatNeedsInfo={isLastOneThatNeedsInfo}
            adminComments={data.comments}
            onSubmit={onSubmitNeedsInfoForm}
            onClose={onClose}
          />
        ) : (
          data && (
            <SupplementResponseDetailsBody
              data={data}
              onShowRiskLevelModal={handleShowRiskLevelModal}
            />
          )
        )}
      </Loadable>
    </CardWithShadow>
  );
};

export default SupplementResponseDetails;
